import { MutableRef, useEffect, useRef, useState } from 'preact/hooks';

const isRightClick = (e: MouseEvent) => {
  return e.button === 2;
};

const isTouchEvent = (e: UIEvent): e is TouchEvent => {
  return 'changedTouches' in e;
};

export const useSliderControl = ({
  sliderRef,
  railRef,
  onSliderValueChange,
  onInteractionStart,
  onInteractionEnd,
}: {
  sliderRef: MutableRef<HTMLElement>;
  railRef: MutableRef<HTMLElement>;
  onSliderValueChange: (value: number) => void;
  onInteractionStart?: () => void;
  onInteractionEnd?: () => void;
}) => {
  const [isActive, setIsActive] = useState(false);

  // Held in refs and updated on every render (rather than passed as effect
  // dependencies) so that a callback identity change mid-drag can't tear
  // down and re-bind the effect below. Re-binding would drop the in-flight
  // window mousemove/mouseup listeners and break the drag gesture. The
  // assignment below is intentionally done in the render phase rather than
  // mirrored via a `useEffect` (the local precedent, e.g.
  // `usePlayerInteraction.ts` and `usePrevious.ts`): these refs are read
  // from synchronous native DOM event handlers, and Preact flushes effects
  // asynchronously, so an effect-mirrored value could still be stale at the
  // moment a handler reads it.
  const onInteractionStartRef = useRef(onInteractionStart);
  onInteractionStartRef.current = onInteractionStart;
  const onInteractionEndRef = useRef(onInteractionEnd);
  onInteractionEndRef.current = onInteractionEnd;

  // Tracks whether the current gesture moved (mousemove/touchmove fired) so
  // the trailing synthetic `click` the browser dispatches after a drag can
  // be ignored, avoiding a redundant duplicate seek. Reset at every gesture
  // start so a drag that ends outside the slider (no click event fired on
  // it) can't leave a stale `true` behind and swallow the next plain click.
  // Declared as a top-level ref (rather than a variable scoped inside the
  // effect body below) so that a mid-drag effect re-bind — reachable for
  // other consumers of this hook, since e.g. `VolumeSlider` passes
  // callbacks that can change identity — cannot reset it back to `false`
  // and resurrect the redundant click-seek.
  const hasDragged = useRef(false);

  useEffect(() => {
    const interactDragHandler = (
      e: HTMLElementEventMap['mousemove' | 'touchmove' | 'click']
    ) => {
      if (e.type === 'click') {
        if (hasDragged.current) {
          hasDragged.current = false;

          return;
        }
      } else if (e.type === 'mousemove' || e.type === 'touchmove') {
        hasDragged.current = true;
      }

      const rect = railRef.current.getBoundingClientRect();

      let dimension: number;
      let percentage: number;

      if (isTouchEvent(e)) {
        dimension = e.changedTouches[0].pageX;
      } else {
        dimension = e.pageX;
      }

      if (dimension < rect.left) {
        percentage = 0;
      } else if (dimension >= rect.right) {
        percentage = 100;
      } else {
        percentage =
          Math.max(Math.min((dimension - rect.left) / rect.width, 1), 0) * 100;
      }

      onSliderValueChange(percentage);
    };

    const interactEndHandler = (
      e: HTMLElementEventMap['mouseup' | 'touchend' | 'touchcancel']
    ) => {
      if (
        e.type === 'mouseup' ||
        e.type === 'touchend' ||
        e.type === 'touchcancel'
      ) {
        setIsActive(false);
        onInteractionEndRef.current?.();
      }

      if (e.type === 'mouseup') {
        window.removeEventListener('mousemove', interactDragHandler);
        window.removeEventListener('mouseup', interactEndHandler);
      } else if (e.type === 'touchend' || e.type === 'touchcancel') {
        window.removeEventListener('touchmove', interactDragHandler);
        window.removeEventListener('touchcancel', interactEndHandler);
        window.removeEventListener('touchend', interactEndHandler);
      }
    };

    const initInteractHandler = (
      e: HTMLElementEventMap['mousedown' | 'touchstart']
    ) => {
      if (!isTouchEvent(e) && isRightClick(e)) {
        return;
      }

      hasDragged.current = false;

      if (e.type === 'mousedown' || e.type === 'touchstart') {
        setIsActive(true);
        onInteractionStartRef.current?.();
      }

      if (e.type === 'mousedown') {
        window.addEventListener('mousemove', interactDragHandler, {
          passive: true,
        });
        window.addEventListener('mouseup', interactEndHandler);
      } else if (e.type === 'touchstart') {
        window.addEventListener('touchmove', interactDragHandler, {
          passive: true,
        });
        window.addEventListener('touchcancel', interactEndHandler);
        window.addEventListener('touchend', interactEndHandler);
      }
    };

    const slider = sliderRef.current;

    slider.addEventListener('mousedown', initInteractHandler);
    slider.addEventListener('touchstart', initInteractHandler, {
      passive: true,
    });
    slider.addEventListener('click', interactDragHandler);

    return () => {
      slider.removeEventListener('mousedown', initInteractHandler);
      slider.removeEventListener('touchstart', initInteractHandler);
      slider.removeEventListener('click', interactDragHandler);

      window.removeEventListener('mousemove', interactDragHandler);
      window.removeEventListener('mouseup', interactEndHandler);
      window.removeEventListener('touchmove', interactDragHandler);
      window.removeEventListener('touchcancel', interactEndHandler);
      window.removeEventListener('touchend', interactEndHandler);
    };
  }, [onSliderValueChange]);

  return { isActive };
};
